import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Character, CharacterClass, Gender } from '@gamedate/core-character/character';
import { apiService } from '../services/api/apiService';
import { useUser } from './UserContext';

interface CharacterContextType {
  characters: Character[];
  activeCharacter: Character | null;
  isLoading: boolean;
  error: string | null;

  // Character management
  fetchCharacters: () => Promise<void>;
  createCharacter: (name: string, characterClass: CharacterClass, gender: Gender) => Promise<Character | null>;
  // B9: characterClass is not accepted — the backend silently drops it on update.
  updateCharacter: (characterId: string, updates: { name?: string; gender?: Gender; appearanceData?: string }) => Promise<Character | null>;
  deleteCharacter: (characterId: string) => Promise<boolean>;
  setActiveCharacter: (characterId: string) => Promise<Character | null>;
  getCharacterWithEquipment: (characterId: string) => Promise<Character | null>;
}

const CharacterContext = createContext<CharacterContextType | undefined>(undefined);

export const MAX_CHARACTERS = 5;

export function CharacterProvider({ children }: { children: ReactNode }) {
  const { user, isLoggedIn, isAuthReady } = useUser();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [activeCharacter, setActiveCharacterState] = useState<Character | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch characters when auth is ready (token is verified)
  useEffect(() => {
    if (isAuthReady && isLoggedIn && user) {
      fetchCharacters();
    } else if (!isLoggedIn) {
      setCharacters([]);
      setActiveCharacterState(null);
    }
  }, [isAuthReady, isLoggedIn, user?.id]);

  const fetchCharacters = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.get<{ characters: Character[] }>('/api/character');

      if (response.error) {
        // API not available - silently fail with empty characters
        // This is expected if the character API isn't implemented yet
        console.log('[CharacterContext] Character API not available:', response.error);
        setCharacters([]);
        setActiveCharacterState(null);
        setIsLoading(false);
        return;
      }

      const chars = response.data?.characters || [];
      // Parse dates
      const parsedChars = chars.map(c => ({
        ...c,
        createdAt: new Date(c.createdAt),
        lastPlayedAt: new Date(c.lastPlayedAt),
      }));

      setCharacters(parsedChars);

      // Set active character
      const active = parsedChars.find(c => c.isActive);
      setActiveCharacterState(active || null);
    } catch (err) {
      // Network error - silently fail
      console.log('[CharacterContext] Failed to fetch characters:', err);
      setCharacters([]);
      setActiveCharacterState(null);
    }

    setIsLoading(false);
  }, []);

  const createCharacter = useCallback(async (
    name: string,
    characterClass: CharacterClass,
    gender: Gender
  ): Promise<Character | null> => {
    if (characters.length >= MAX_CHARACTERS) {
      setError(`Maximum of ${MAX_CHARACTERS} characters allowed`);
      return null;
    }

    setIsLoading(true);
    setError(null);

    const response = await apiService.post<Character>('/api/character', {
      name,
      class: characterClass === 'tank' ? 0 : characterClass === 'archer' ? 1 : characterClass === 'mage' ? 2 : 3,
      gender,
    });

    if (response.error) {
      setError(response.error);
      setIsLoading(false);
      return null;
    }

    const newChar = response.data!;
    const parsedChar = {
      ...newChar,
      createdAt: new Date(newChar.createdAt),
      lastPlayedAt: new Date(newChar.lastPlayedAt),
    };

    setCharacters(prev => [...prev, parsedChar]);

    // If this is the first character, it's automatically active
    if (parsedChar.isActive) {
      setActiveCharacterState(parsedChar);
    }

    setIsLoading(false);
    return parsedChar;
  }, [characters.length]);

  const updateCharacter = useCallback(async (
    characterId: string,
    updates: { name?: string; gender?: Gender; appearanceData?: string }
  ): Promise<Character | null> => {
    setIsLoading(true);
    setError(null);

    const response = await apiService.put<Character>(`/api/character/${characterId}`, updates);

    if (response.error) {
      setError(response.error);
      setIsLoading(false);
      return null;
    }

    const updatedChar = response.data!;
    const parsedChar = {
      ...updatedChar,
      createdAt: new Date(updatedChar.createdAt),
      lastPlayedAt: new Date(updatedChar.lastPlayedAt),
    };

    setCharacters(prev => prev.map(c => c.id === characterId ? parsedChar : c));

    if (parsedChar.isActive) {
      setActiveCharacterState(parsedChar);
    }

    setIsLoading(false);
    return parsedChar;
  }, []);

  const deleteCharacter = useCallback(async (characterId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    const response = await apiService.delete(`/api/character/${characterId}`);

    if (response.error) {
      setError(response.error);
      setIsLoading(false);
      return false;
    }

    setCharacters(prev => {
      const remaining = prev.filter(c => c.id !== characterId);

      // If we deleted the active character, the server will have set another as active
      // We should refetch to get the updated state
      if (activeCharacter?.id === characterId) {
        fetchCharacters();
      }

      return remaining;
    });

    setIsLoading(false);
    return true;
  }, [activeCharacter?.id, fetchCharacters]);

  const setActiveCharacter = useCallback(async (characterId: string): Promise<Character | null> => {
    setIsLoading(true);
    setError(null);

    const response = await apiService.post<Character>(`/api/character/${characterId}/activate`);

    if (response.error) {
      setError(response.error);
      setIsLoading(false);
      return null;
    }

    const activatedChar = response.data!;
    const parsedChar = {
      ...activatedChar,
      createdAt: new Date(activatedChar.createdAt),
      lastPlayedAt: new Date(activatedChar.lastPlayedAt),
    };

    // Update all characters' isActive status
    setCharacters(prev => prev.map(c => ({
      ...c,
      isActive: c.id === characterId
    })));

    setActiveCharacterState(parsedChar);

    setIsLoading(false);
    return parsedChar;
  }, []);

  const getCharacterWithEquipment = useCallback(async (characterId: string): Promise<Character | null> => {
    const response = await apiService.get<Character>(`/api/character/${characterId}`);

    if (response.error) {
      setError(response.error);
      return null;
    }

    const char = response.data!;
    return {
      ...char,
      createdAt: new Date(char.createdAt),
      lastPlayedAt: new Date(char.lastPlayedAt),
    };
  }, []);

  return (
    <CharacterContext.Provider value={{
      characters,
      activeCharacter,
      isLoading,
      error,
      fetchCharacters,
      createCharacter,
      updateCharacter,
      deleteCharacter,
      setActiveCharacter,
      getCharacterWithEquipment,
    }}>
      {children}
    </CharacterContext.Provider>
  );
}

export function useCharacter() {
  const context = useContext(CharacterContext);
  if (!context) {
    throw new Error('useCharacter must be used within a CharacterProvider');
  }
  return context;
}
